/**
 * Logs API Client
 * Fetches user activity logs from the Cloudflare Worker
 */

import { getStoredSessionId } from './auth-client';

const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';
const API_BASE = CF_WORKER_URL ? `${CF_WORKER_URL}/api` : '/api';

export type LogType = 'audit' | 'action' | 'system';

export interface LogEntry {
  id: string;
  log_type: LogType;
  event_type: string;
  user_id: string | null;
  session_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  request_method: string | null;
  request_path: string | null;
  resource_type: string | null;
  resource_id: string | null;
  details: string | null;
  previous_state: string | null;
  new_state: string | null;
  affected_count: number;
  status: 'success' | 'failure' | 'pending';
  error_message: string | null;
  duration_ms: number | null;
  created_at: string;
}

export interface LogsResponse {
  logs: LogEntry[];
  pagination?: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  total?: number;
}

export interface LogStats {
  byEventType: Array<{
    log_type: string;
    event_type: string;
    status: string;
    count: number;
  }>;
  dailyActivity: Array<{
    date: string;
    log_type: string;
    count: number;
  }>;
}

function getAuthHeaders(): HeadersInit {
  const sessionId = getStoredSessionId();
  return sessionId ? { 'X-Session-Id': sessionId } : {};
}

/**
 * Fetch logs with optional filters
 */
export async function fetchLogs(options: {
  logType?: LogType;
  limit?: number;
  offset?: number;
} = {}): Promise<LogsResponse> {
  const params = new URLSearchParams();
  if (options.logType) params.set('logType', options.logType);
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.offset) params.set('offset', options.offset.toString());

  const response = await fetch(`${API_BASE}/logs?${params}`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch logs');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Fetch user's audit trail
 */
export async function fetchAuditLogs(options: {
  limit?: number;
  offset?: number;
} = {}): Promise<LogsResponse> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.offset) params.set('offset', options.offset.toString());

  const response = await fetch(`${API_BASE}/logs/audit?${params}`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch audit logs');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Fetch user's action history
 */
export async function fetchActionLogs(options: {
  limit?: number;
  offset?: number;
} = {}): Promise<LogsResponse> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.offset) params.set('offset', options.offset.toString());

  const response = await fetch(`${API_BASE}/logs/actions?${params}`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch action logs');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Fetch log statistics
 */
export async function fetchLogStats(): Promise<LogStats> {
  const response = await fetch(`${API_BASE}/logs/stats`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch log stats');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Export logs as JSON
 */
export async function exportLogs(options: {
  logType?: LogType;
  startDate?: string;
  endDate?: string;
} = {}): Promise<Blob> {
  const params = new URLSearchParams();
  if (options.logType) params.set('logType', options.logType);
  if (options.startDate) params.set('startDate', options.startDate);
  if (options.endDate) params.set('endDate', options.endDate);

  const response = await fetch(`${API_BASE}/logs/export?${params}`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to export logs');
  }

  return response.blob();
}

/**
 * Format event type for display
 */
export function formatEventType(eventType: string): string {
  const labels: Record<string, string> = {
    // Audit events
    'auth.login': 'Signed in',
    'auth.logout': 'Signed out',
    'auth.login_failed': 'Login failed',
    'auth.token_refresh': 'Token refreshed',
    'account.created': 'Account created',
    // Action events
    'email.bulk_delete': 'Deleted emails',
    'email.bulk_archive': 'Archived emails',
    'email.bulk_unsubscribe': 'Blocked senders',
    'email.unsubscribed': 'Sender blocked',
    'email.replied': 'Reply sent',
    'email.deleted': 'Email deleted',
    'email.archived': 'Email archived',
    'drive.file_saved': 'File saved to Drive',
    // System events
    'sync.started': 'Sync started',
    'sync.completed': 'Sync completed',
    'sync.failed': 'Sync failed',
    'sync.cancelled': 'Sync cancelled',
    'sync.refresh_started': 'Refresh started',
    'sync.refresh_completed': 'Refresh completed',
    'sync.refresh_failed': 'Refresh failed',
  };

  return labels[eventType] || eventType.replace(/[._]/g, ' ');
}

/**
 * Parse log timestamps from D1 (UTC, no timezone info) safely.
 */
export function parseLogTimestamp(createdAt: string): Date {
  const createdAtStr = String(createdAt).trim();
  if (!createdAtStr) {
    return new Date(NaN);
  }

  const hasTimezone =
    createdAtStr.includes('Z') || /[+-]\d{2}:?\d{2}$/.test(createdAtStr);
  if (hasTimezone) {
    const direct = new Date(createdAtStr);
    if (!isNaN(direct.getTime())) return direct;
  }

  const normalized = createdAtStr.includes('T')
    ? createdAtStr
    : createdAtStr.replace(' ', 'T');
  const utcDate = new Date(`${normalized}Z`);
  if (!isNaN(utcDate.getTime())) return utcDate;

  return new Date(createdAtStr);
}

/**
 * Get icon for event type
 */
export function getEventIcon(eventType: string): string {
  if (eventType.startsWith('auth.login') || eventType === 'account.created') return '🔐';
  if (eventType === 'auth.logout') return '👋';
  if (eventType.includes('delete')) return '🗑️';
  if (eventType.includes('archive')) return '📦';
  if (eventType.includes('reply')) return '↩️';
  if (eventType.includes('unsubscribe')) return '📭';
  if (eventType.includes('drive')) return '📁';
  if (eventType.includes('sync')) return '🔄';
  return '📋';
}
